import * as React from "react";

import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Button from "@/components/button.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import type { WizardStep } from "@/components/wizard";
import Wizard from "@/components/wizard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth.tsx";
import { isApiError } from "@/queries/api/apiUtils.ts";
import { getBusinessProfile } from "@/queries/api/businessApi.ts";
import { getCertifications } from "@/queries/api/certificationApi.ts";
import { getProfile } from "@/queries/api/profileApi.ts";
import { QueryKeys } from "@/queries/queryKeys.ts";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger.ts";
import { captureEvent } from "@/lib/analytics/posthog";
import { useBillingEnabled } from "@/queries/hooks/billingHooks";
import { signupDetailsCompletedEvent } from "@/lib/analytics/events";

import { ProfileProvider } from "./-profile-context.tsx";
import { WorkerOnboardingProvider } from "./-worker-onboarding-context.tsx";
import { OwnerOnboardingProvider } from "./-owner-onboarding-context.tsx";
import {
  PersonalInfoStep,
  WorkExperience,
  CertificationsStep,
  BankDetailsStep,
  WorkerBankDetailsStep,
  TaxDocumentsStep,
  AvailabilityStep,
  SkillsStep,
  BusinessProfile,
  ConfirmDetails,
  WorkerConfirmDetailsStep,
  WorkerRoleExperienceStep,
  WorkerLocationStep,
  WorkerResumePhotoStep,
  WorkerReviewStep,
  WorkerLegalAgreementsStep,
  OwnerRestaurantStep,
  OwnerAboutYouStep,
  OwnerLegalAgreementsStep,
  OwnerBillingStep,
} from "./-steps/index.tsx";

const NEW_WORKER_ONBOARDING_ENABLED =
  import.meta.env.VITE_NEW_WORKER_ONBOARDING === "true";

const NEW_EMPLOYER_ONBOARDING_ENABLED =
  import.meta.env.VITE_NEW_EMPLOYER_ONBOARDING === "true";

type BackendFieldError = { field: string; message: string };

/** Pull fieldErrors out of a 422 ValidationErrorResponse body, if present. */
function extractFieldErrors(err: unknown): Array<BackendFieldError> {
  if (!isApiError(err)) return [];
  const body = err.body;
  if (!body || typeof body !== "object") return [];
  const fes = (body as { fieldErrors?: unknown }).fieldErrors;
  if (!Array.isArray(fes)) return [];
  return fes
    .filter(
      (x): x is BackendFieldError =>
        x !== null &&
        typeof x === "object" &&
        typeof (x as BackendFieldError).field === "string" &&
        typeof (x as BackendFieldError).message === "string",
    )
    .map((x) => ({ field: x.field, message: x.message }));
}

/** Maps BE field paths (e.g. "location.city", "roleInterests") to step labels. */
function friendlyField(field: string): string {
  if (field.startsWith("location")) return "Location";
  if (field === "roleInterests" || field.startsWith("roleInterests"))
    return "Experience";
  if (field === "fullName") return "Name";
  if (field === "restaurant") return "Restaurant";
  if (field === "contact") return "About you";
  if (field === "billing") return "Billing";
  if (field === "termsAccepted") return "Legal agreements";
  return field;
}

export const Route = createFileRoute("/app/$role/setup/")({
  loader: async ({ context }) => {
    const qc = context.queryClient;
    const { role } = context.auth?.user ?? { role: "worker" };

    if (role === "employer") {
      if (NEW_EMPLOYER_ONBOARDING_ENABLED) {
        // New owner onboarding lazy-loads via OwnerOnboardingProvider; legacy
        // /business/info would 400 for fresh employers and trigger the route
        // errorComponent.
      } else {
        await Promise.all([
          qc.ensureQueryData({
            queryKey: [QueryKeys.BUSINESS_PROFILE],
            queryFn: getBusinessProfile,
          }),
        ]);
      }
    } else if (NEW_WORKER_ONBOARDING_ENABLED) {
      // New worker onboarding owns its own data load via WorkerOnboardingProvider
      // (useOnboardingDraft fires on mount and lazy-creates the profile).
      // Prefetching /api/user/profile/info here would 400 for fresh workers and
      // trigger the route errorComponent ("Something broke while loading this page").
    } else {
      await Promise.all([
        qc.ensureQueryData({
          queryKey: [QueryKeys.PROFILE],
          queryFn: getProfile,
        }),
        qc.ensureQueryData({
          queryKey: [QueryKeys.CERTIFICATIONS],
          queryFn: getCertifications,
        }),
      ]);
    }
  },
  component: SetupPage,
});

export default function SetupPage() {
  const { t } = useTranslation("onboarding");
  const auth = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const { role } = auth.user || { role: "worker" };
  // Billing is per-account opt-in: build flag on AND billing/status.enabled.
  // While the first status fetch is in flight (flag on, employer) we hold the
  // wizard so steps don't jump and the Stripe-redirect resume lands correctly.
  const billing = useBillingEnabled();
  const billingVisible = billing.enabled;

  const workerSteps: Array<WizardStep> = React.useMemo(
    () =>
      NEW_WORKER_ONBOARDING_ENABLED
        ? [
            { title: t("steps.confirmDetails"), step: WorkerConfirmDetailsStep },
            { title: t("steps.experience"), step: WorkerRoleExperienceStep },
            { title: t("steps.location"), step: WorkerLocationStep },
            { title: t("steps.resumePhoto"), step: WorkerResumePhotoStep },
            { title: t("steps.review"), step: WorkerReviewStep },
            {
              title: t("steps.legalAgreements"),
              step: WorkerLegalAgreementsStep,
            },
          ]
        : [
            { title: t("steps.personalInfo"), step: PersonalInfoStep },
            { title: t("steps.workExperience"), step: WorkExperience },
            { title: t("steps.skills"), step: SkillsStep },
            { title: t("steps.certifications"), step: CertificationsStep },
            { title: t("steps.availability"), step: AvailabilityStep },
            { title: t("steps.taxDocuments"), step: TaxDocumentsStep },
            { title: t("steps.bankAccountSetup"), step: WorkerBankDetailsStep },
          ],
    [t],
  );

  const employerSteps: Array<WizardStep> = React.useMemo(
    () =>
      NEW_EMPLOYER_ONBOARDING_ENABLED
        ? [
            { title: t("steps.aboutRestaurant"), step: OwnerRestaurantStep },
            { title: t("steps.aboutYou"), step: OwnerAboutYouStep },
            {
              title: t("steps.legalAgreements"),
              step: OwnerLegalAgreementsStep,
            },
            // Billing step (Payment Element → SetupIntent) only for owners with
            // billing visible (flag + per-account opt-in); /complete then starts
            // the subscription. Must stay last: getInitialStep() resumes here
            // after a Stripe redirect.
            ...(billingVisible
              ? [{ title: t("steps.billing"), step: OwnerBillingStep }]
              : []),
          ]
        : [
            { title: t("steps.businessProfile"), step: BusinessProfile },
            { title: t("steps.confirmDetails"), step: ConfirmDetails },
            { title: t("steps.stripeConnectSetup"), step: BankDetailsStep },
          ],
    [t, billingVisible],
  );
  const isEditMode = Boolean(auth.user?.onboardingCompleted);
  const useNewWorkerFlow = NEW_WORKER_ONBOARDING_ENABLED && role === "worker";
  const useNewOwnerFlow = NEW_EMPLOYER_ONBOARDING_ENABLED && role === "employer";

  const onComplete = async () => {
    try {
      // First-time onboarding: hit /complete so the BE re-validates and Keycloak
      // flips onboarding_complete. Edit mode: the final step's onContinue already
      // called saveDraft, so just refresh caches and route back.
      if (!isEditMode) {
        await auth.completeOnboarding();
        // Funnel step 3 (T2 / APPENDIX §B): profile (worker) / business
        // (employer) details submitted. Only on first-time onboarding, never on
        // an edit-mode "Save Changes". The authoritative terminal event
        // (`signup_completed`) is fired server-side (T3).
        captureEvent(signupDetailsCompletedEvent(role));
      }
      await router.invalidate();
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!isEditMode && useNewOwnerFlow) {
        // New owners finish on the bulk "add workers" page (send invites or
        // skip to the dashboard).
        navigate({
          to: "/app/$role/roster/add",
          params: { role },
          search: { onboarding: true },
          replace: true,
        });
        return;
      }
      navigate({
        to: "/app/$role/dashboard",
        params: { role },
        replace: true,
      });
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      Logger.error("Onboarding completion failed", {
        error: e instanceof Error ? e.message : String(e),
        stack: e instanceof Error ? e.stack : undefined,
        fieldErrors,
      });
      if (fieldErrors.length > 0) {
        // BE 422 with specific completeness failures. Show each so the user
        // knows exactly which step to revisit instead of the generic message.
        for (const fe of fieldErrors) {
          notify.error(`${friendlyField(fe.field)}: ${fe.message}`);
        }
      } else {
        notify.error(
          e instanceof Error ? e.message : t("completionError"),
        );
      }
      // Don't redirect to dashboard - let user retry
    }
  };

  const onSkip = async () => {
    try {
      // Ensure auth state reflects completion so route guard allows dashboard.
      // Edit mode is already complete: leave without re-running /complete (with
      // subscription billing on it requires a payment method and would start a
      // subscription).
      if (!isEditMode) await auth.completeOnboarding();
      await router.invalidate();
      await navigate({
        to: "/app/$role/dashboard",
        params: { role },
      });
    } catch (e) {
      Logger.error("onboarding skip failed", {
        error: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const steps = role === "worker" ? workerSteps : employerSteps;

  const getInitialStep = () => {
    if (role !== "employer") return 0;
    const urlParams = new URLSearchParams(window.location.search);
    if (billingVisible && useNewOwnerFlow) {
      // Back from a Stripe redirect in the billing step (always the last step);
      // OwnerBillingStep confirms the SetupIntent.
      return urlParams.get("setup_intent") ? steps.length - 1 : 0;
    }
    if (urlParams.get('setup_intent') && urlParams.get('redirect_status') === 'succeeded') {
      return 2; // Stripe Connect Setup step
    }
    return 0;
  };

  const initialStep = getInitialStep();
  console.log({ initialStep })

  const ProviderWrapper: React.ComponentType<{ children: React.ReactNode }> =
    useNewWorkerFlow
      ? WorkerOnboardingProvider
      : useNewOwnerFlow
        ? OwnerOnboardingProvider
        : ProfileProvider;

  if (useNewOwnerFlow && billing.isLoading) {
    return (
      <div className="w-full max-w-[783px] md:pl-16">
        <p className="body-small text-gray-500 p-6">{t("form.loading", { ns: "billing" })}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[783px] md:pl-16">
      <ProviderWrapper>
        <Wizard
          steps={steps}
          initialStep={initialStep}
          onComplete={onComplete}
          completeCheckComponent={function CompleteActions({
            onComplete: handleComplete,
          }: {
            onComplete: () => Promise<void>;
          }) {
            const [agree, setAgree] = React.useState(false);
            const [busy, setBusy] = React.useState(false);
            const [termsModalOpen, setTermsModalOpen] = React.useState(false);
            const [termsAccepted, setTermsAccepted] = React.useState(false);

            if (isEditMode) {
              // Returning user editing their profile: terms were accepted at
              // first-time onboarding, and the BE /complete call is a no-op,
              // so the gate is just friction. Render a single Save Changes button.
              return (
                <div className="flex gap-4 flex-row w-full justify-between items-center mt-20">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (busy) return;
                      setBusy(true);
                      try {
                        await handleComplete();
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    className="w-[320px]"
                  >
                    {busy ? t("saving") : t("saveChanges")}
                  </Button>
                  <Button variant="link" onClick={onSkip} disabled={busy}>
                    {t("cancel")}
                  </Button>
                </div>
              );
            }

            if (useNewWorkerFlow || useNewOwnerFlow) {
              // Consent is gated by the Legal Agreements step itself
              // (Worker/OwnerLegalAgreementsStep registers an onContinue
              // handler that fails until both T&C and Privacy Policy
              // checkboxes are checked, and the Owner variant also persists
              // acceptance server-side). Avoid a second redundant gate.
              return (
                <div className="flex gap-4 flex-row w-full justify-between items-center mt-20">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (busy) return;
                      setBusy(true);
                      try {
                        await handleComplete();
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    className="w-[320px]"
                  >
                    {busy ? t("completing") : t("completeOnboarding")}
                  </Button>
                  {/* With subscription billing on, owners can't finish without a
                      payment method, so there's nothing to skip to. */}
                  {!(useNewOwnerFlow && billingVisible) && (
                    <Button variant="link" onClick={onSkip} disabled={busy}>
                      {t("skipForNow")}
                    </Button>
                  )}
                </div>
              );
            }

            return (
              <>
                <label
                  htmlFor="onboarding-terms-agree"
                  className={
                    "flex flex-row gap-3 items-start p-4 rounded-md ring-1 cursor-pointer transition-colors " +
                    (agree
                      ? "ring-garnet/30 bg-garnet/5"
                      : "ring-garnet bg-garnet/10 hover:bg-garnet/15")
                  }
                >
                  <Checkbox
                    id="onboarding-terms-agree"
                    checked={agree}
                    onCheckedChange={(v) => setAgree(Boolean(v))}
                    className="size-5 mt-0.5 shrink-0 border-2 border-garnet data-[state=checked]:bg-garnet"
                  />
                  <span className="body-small">
                    {t("termsAgreement")}
                    <button
                      type="button"
                      className="text-primary underline"
                      onClick={(e) => {
                        e.preventDefault();
                        setTermsModalOpen(true);
                      }}
                    >
                      {t("termsOfConditions")}
                    </button>
                    .
                  </span>
                </label>
                <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{t("termsModalTitle")}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-sm text-muted-foreground">
                        {t("placeholderTerms")}
                      </p>
                      <p className="text-sm text-muted-foreground mt-4">
                        {t("placeholderTerms2")}
                      </p>
                    </div>
                    <DialogFooter className="flex flex-col gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="terms-accept"
                          checked={termsAccepted}
                          onCheckedChange={(v) => setTermsAccepted(Boolean(v))}
                        />
                        <label
                          htmlFor="terms-accept"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {t("acceptTerms")}
                        </label>
                      </div>
                      <Button
                        onClick={() => {
                          if (termsAccepted) {
                            setAgree(true);
                            setTermsModalOpen(false);
                          }
                        }}
                        disabled={!termsAccepted}
                      >
                        {t("confirm")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <div className="flex gap-4 flex-row w-full justify-between items-center mt-20">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (busy) return;
                      if (!agree) {
                        notify.error(t("mustAcceptTerms"));
                        const el = document.getElementById(
                          "onboarding-terms-agree",
                        );
                        el?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                        el?.focus();
                        return;
                      }
                      setBusy(true);
                      try {
                        await handleComplete();
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    className="w-[320px]"
                  >
                    {busy ? t("completing") : t("completeOnboarding")}
                  </Button>
                  <Button variant="link" onClick={onSkip} disabled={busy}>
                    {t("skipForNow")}
                  </Button>
                </div>
              </>
            );
          }}
        />
      </ProviderWrapper>
    </div>
  );
}
